import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import React from "react";
import { get } from "react-hook-form";
import { useFormState } from "../Form/Form";
import Label from "../Label/Label";
import styles from "./Field.module.scss";

export const useSyntheticFieldRef = <TElement extends HTMLElement, TValue>(
  fieldRef: ((instance: unknown) => void) | undefined,
  value: TValue,
  coerceValue: (value: unknown) => TValue = (nextValue) => nextValue as TValue,
): React.RefCallback<TElement> => {
  const elementRef = React.useRef<TElement | null>(null);
  const fieldRefRef = React.useRef(fieldRef);
  const valueRef = React.useRef(value);
  const coerceValueRef = React.useRef(coerceValue);
  fieldRefRef.current = fieldRef;
  valueRef.current = value;
  coerceValueRef.current = coerceValue;

  const syntheticRef = React.useMemo(
    () => ({
      // RHF reads `ref.value` on submit/registration. Use this when the visual/control
      // element has an incompatible native value. Example: Radix Checkbox renders a
      // button with native `value="on"`; without a synthetic boolean value, a
      // conditionally mounted CheckboxField can submit `"on"` instead of `false`.
      get value() {
        return valueRef.current;
      },
      set value(nextValue: unknown) {
        valueRef.current = coerceValueRef.current(nextValue);
      },
      get name() {
        return elementRef.current?.getAttribute("name") ?? "";
      },
      get disabled() {
        return Boolean((elementRef.current as any)?.disabled);
      },
      focus: () => elementRef.current?.focus(),
      blur: () => elementRef.current?.blur(),
      click: () => elementRef.current?.click(),
      reportValidity: () => (elementRef.current as any)?.reportValidity?.() ?? true,
      setCustomValidity: (message: string) => (elementRef.current as any)?.setCustomValidity?.(message),
    }),
    [],
  );

  return React.useCallback(
    (element: TElement | null) => {
      elementRef.current = element;
      fieldRefRef.current?.(element ? syntheticRef : null);
    },
    [syntheticRef],
  );
};

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  name?: string;
  fullWidth?: boolean;
}

const FieldContext = React.createContext<{ name?: string; id: string; fullWidth: boolean }>({
  id: "",
  fullWidth: false,
});

const Field = ({ children, className, name, fullWidth = false, ...props }: FieldProps) => {
  const id = React.useId();
  return (
    <FieldContext.Provider value={{ name, id, fullWidth }}>
      <div
        className={clsx(styles.field, className)}
        data-full-width={fullWidth ? "true" : "false"}
        {...props}
      >
        {children}
      </div>
    </FieldContext.Provider>
  );
};

export interface FieldLabelProps extends React.ComponentPropsWithoutRef<typeof Label> {
  children: React.ReactNode;
}

const FieldLabel = ({ children, ...props }: FieldLabelProps) => {
  const { id } = React.useContext(FieldContext);
  return (
    <Label
      htmlFor={id}
      className={styles.label}
      {...props}
    >
      {children}
    </Label>
  );
};

export interface FieldControlProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const FieldControl = ({ children, className, ...props }: FieldControlProps) => {
  const { id, name, fullWidth } = React.useContext(FieldContext);
  const formState = useFormState({ name: name as any });
  const error = formState ? get(formState.errors, name) : undefined;
  const invalid = Boolean(error);
  const describedBy = error ? `${id}-message` : undefined;
  return (
    <Slot
      id={id}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      className={clsx(styles.control, className)}
      data-field-control
      data-full-width={fullWidth ? "true" : "false"}
      {...props}
    >
      {children}
    </Slot>
  );
};

export interface FieldMessageProps extends React.HTMLAttributes<HTMLSpanElement> {
  error?: boolean;
  children?: React.ReactNode;
  name?: string;
  message?: React.ReactNode;
  as?: React.ReactElement | React.ComponentType<any> | keyof React.JSX.IntrinsicElements;
  render?: (props: { message: React.ReactNode; messages?: unknown }) => React.ReactElement;
}

const FieldMessage = ({ error, children, className, name, message, as, render, ...props }: FieldMessageProps) => {
  "use no memo";

  const { id, name: fieldName } = React.useContext(FieldContext);
  const resolvedName = name ?? fieldName;
  const formState = useFormState({ name: resolvedName as any });
  const fieldError = resolvedName && formState ? get(formState?.errors, resolvedName) : undefined;

  if (!children && !fieldError && !message) {
    return null;
  }

  const messageFromRegister = fieldError?.message;
  const resolvedMessage = children ?? messageFromRegister ?? message;
  const resolvedError = Boolean(error ?? fieldError);
  const messageProps = Object.assign({}, props, {
    id: `${id}-message`,
    className: clsx(styles.helperText, className),
    style: { color: resolvedError ? "var(--destructive)" : "var(--muted-foreground)", ...props.style },
    children: resolvedMessage,
  });

  if (render && (messageFromRegister || message)) {
    return render({ message: messageFromRegister || message, messages: fieldError?.types });
  }

  if (React.isValidElement(as)) {
    return React.cloneElement(as, messageProps);
  }

  if (as) {
    return React.createElement(as as any, messageProps);
  }

  return <span {...messageProps} />;
};

export default Object.assign(Field, {
  Label: FieldLabel,
  Control: FieldControl,
  Message: FieldMessage,
  HelperText: FieldMessage,
});
